import React from "react";
import styled from "styled-components";
import { RemoveCircle } from "@mui/icons-material";
import { Link } from "react-router-dom";

import { routes } from "../helpers/RouteHelpers";
import { HeadedCard } from "./HeadedCard";

export const labels = {
    valuationDate: "Valuation as at",
    gainLoss: "Gain / loss",
    moneyIn: "Money in",
    moneyOut: "Money out",
    paymentsWithdrawals: "Payments / Withdrawals"
};

const valuation = () => ({
    header: <Heading>{labels.valuationDate}</Heading>,
    body: (
        <>
            <Paragraph>
                Assets on the platform are typically priced at the end of each trading day so the
                valuation that we show today is from close of business the previous day.
            </Paragraph>
            <Paragraph>
                Where the previous day is a non-trading day (like a weekend or a bank holiday), we
                still update the valuation in Nucleus Go, but the prices used will be the same as
                the end of the last trading day.
            </Paragraph>
        </>
    )
});

export const pages = {
    accountDetails: "accountDetails",
    analysis: "analysis"
};

const gainLoss = ({ startDate, endDate, accountNumber, currentPage }) => ({
    header: <Heading>{labels.gainLoss}</Heading>,
    body: (
        <>
            <Paragraph>
                Amount of money you have gained or lost on your investments
                {startDate && endDate ? ` between ${startDate} and ${endDate}` : null}.{" "}
            </Paragraph>
            <Paragraph>
                This includes the effect of fees, which are considered a drag on performance, along
                with interest and distributions, which are treated as an increase in performance.
            </Paragraph>
            <Paragraph>
                If you want to calculate this figure yourself, you can find the full breakdown of
                money in and out{" "}
                {currentPage === pages.accountDetails ? (
                    <>
                        on the{" "}
                        <StyledLink
                            to={routes.client.analysis({
                                accountNum: accountNumber,
                                rangeId: "ALL_DATES"
                            })}
                        >
                            analysis page.
                        </StyledLink>
                    </>
                ) : (
                    "on this page."
                )}
            </Paragraph>

            <HeadedCard title="How it's calculated">
                <Formula>
                    <FormulaParagraph>closing balance</FormulaParagraph>
                    <FormulaIcon>
                        <RemoveCircle fontSize="small" aria-label="minus" />
                    </FormulaIcon>
                    <FormulaParagraph>opening balance</FormulaParagraph>
                    <FormulaIcon>
                        <RemoveCircle fontSize="small" aria-label="minus" />
                    </FormulaIcon>
                    <div>
                        <FormulaParagraph>money in</FormulaParagraph>
                        <SmallFormulaParagraph>
                            (excluding distributions and rebates)
                        </SmallFormulaParagraph>
                    </div>
                    <FormulaIcon>
                        <RemoveCircle fontSize="small" aria-label="minus" />
                    </FormulaIcon>
                    <div>
                        <FormulaParagraph>money out</FormulaParagraph>
                        <SmallFormulaParagraph>(excluding fees)</SmallFormulaParagraph>
                    </div>
                    <FormulaIcon>
                        <RemoveCircle fontSize="small" aria-label="minus" />
                    </FormulaIcon>
                    <FormulaParagraph>adjustments and inter-account transfers</FormulaParagraph>
                </Formula>
            </HeadedCard>
        </>
    )
});

const moneyIn = () => ({
    header: <Heading>{labels.moneyIn}</Heading>,
    body: (
        <>
            <Paragraph>
                Includes the following transactions (if applicable to your account):
            </Paragraph>
            <PaddedList>
                <li>Lump sum payment</li>
                <li>Re-registration in</li>
                <li>Cash transfer in</li>
                <li>Regular payments</li>
                <li>Pension tax relief</li>
                <li>Interest distributions</li>
                <li>Income distributions</li>
                <li>Rebates</li>
            </PaddedList>
        </>
    )
});

const moneyOut = () => ({
    header: <Heading>{labels.moneyOut}</Heading>,
    body: (
        <>
            <Paragraph>
                Includes the following transactions (if applicable to your account):
            </Paragraph>
            <PaddedList>
                <li>Cash transfer out</li>
                <li>Lump sum withdrawal</li>
                <li>Investment fees</li>
                <li>Re-registration out</li>
                <li>Regular withdrawal</li>
                <li>Natural income</li>
                <li>Adviser fees</li>
                <li>Platform fees</li>
            </PaddedList>
        </>
    )
});

const paymentsWithdrawals = () => ({
    header: <Heading>{labels.paymentsWithdrawals}</Heading>,
    body: (
        <>
            <Paragraph>
                This figure is the sum of all payments in less the sum of all withdrawals out,
                including the following transaction types:
            </Paragraph>
            <PaddedList>
                <li>
                    Payments: lump sum payment, inter account transfer in, re-registration in, cash
                    transfer in, regular payments, pension tax relief
                </li>
                <li>
                    Withdrawals: cash transfer out, inter account transfer out, lump sum withdrawal,
                    re-registration out, regular withdrawal, natural income
                </li>
            </PaddedList>
            <Paragraph>
                Please note that this calculation is performed on a gross basis and does not include
                the impact of fees, rebates, distributions and interest payments which are treated
                as impacts on performance and counted in the valuation figure. The exception to this
                is trading fees, the effects of which are included in the figure.
            </Paragraph>
        </>
    )
});

const descriptions = {
    [labels.gainLoss]: gainLoss,
    [labels.moneyIn]: moneyIn,
    [labels.moneyOut]: moneyOut,
    [labels.valuationDate]: valuation,
    [labels.paymentsWithdrawals]: paymentsWithdrawals
};

export const hasDescription = label => Boolean(Object.keys(descriptions).includes(label));

export const getDescription = (label, additionalData = {}) => descriptions[label](additionalData);

const PaddedList = styled.ul`
    padding-left: 1rem;
`;

const Heading = styled.h1`
    font-size: 1.25rem;
`;

const Paragraph = styled.p`
    font-size: 1rem;
    text-align: justify;
`;

const Formula = styled.div`
    display: flex;
    align-items: center;
    justify-content: center;
    text-align: center;

    @media (max-width: 620px) {
        flex-direction: column;
    }
`;

const FormulaIcon = styled.div`
    display: flex;
    margin: 1rem;

    @media (max-width: 620px) {
        margin: 0.5rem;
    }
`;

const FormulaParagraph = styled.p`
    font-size: 0.875rem;
    margin: 0;
`;

const SmallFormulaParagraph = styled.p`
    font-size: 0.75rem;
    margin: 0;
`;

const StyledLink = styled(Link)`
    color: ${props => props.theme.secondary};
`;
